import React, {CSSProperties, forwardRef, useCallback, useEffect, useImperativeHandle, useState} from "react";
import useSWRInfinite from "swr/infinite";
import "./list.css";
import {fetcher} from "api/fetcher";
import {FixedSizeList} from 'react-window';
import {GetListResponse} from "shared/types";
import {LoadingSpinner} from "shared/assets/svgs/LoadingSpinner";

export interface InfiniteListProps<T> {
  params?: string;
  url?: string | null;
  data?: T[];
  pageSize?: number;
  height?: number;
  itemSize?: number;
  shouldRender?: (item: T) => boolean;
  renderItem: (item: T, style: CSSProperties) => React.ReactNode;
  onGetCount?: (count: number, data: any[]) => void;
  onRefresh?: () => void;
}

function InfiniteList<T>({
                           renderItem,
                           data: dataProp,
                           url,
                           shouldRender,
                           params,
                           height = 200,
                           itemSize = 20,
                           pageSize = 300,
                           onRefresh,
                           onGetCount
                         }: InfiniteListProps<T>, ref) {
  const [listData, setListData] = useState<T[]>([]);
  
  const getKey = (pageIndex: number, previousPageData?: GetListResponse) => {
    if (pageIndex === 0 && url) {
      if (params) {
        return `${url}?page_size=${pageSize}&${params}`;
      }
      return url;
    }
    if (previousPageData && previousPageData.next) {
      return previousPageData.next;
    }
    return null; // reached the end
  };
  
  const {
          data, setSize, mutate, isLoading
        } = useSWRInfinite<GetListResponse>(getKey, fetcher, {revalidateFirstPage: true});
  
  const requestRefresh = useCallback(() => {
    mutate();
    if (onRefresh) {
      onRefresh();
    }
  }, [mutate, onRefresh]);
  
  useImperativeHandle(ref, () => ({
    requestRefresh,
  }), [requestRefresh],);
  
  useEffect(() => {
    let listData: T[] = [];
    if (dataProp) {
      listData = dataProp;
    }
    else if (data && data?.length > 0) {
      data.forEach((item) => {
        if (item?.results) {
          const filtered = shouldRender ? item?.results?.filter(plan => shouldRender(plan)) : item?.results;
          listData = listData.concat(filtered);
        }
      });
    }
    !!onGetCount && onGetCount(data && data?.length > 0 ? data[0]?.count : 0, listData);
    setListData(listData);
  }, [data, dataProp, onGetCount, setSize, shouldRender, url]);
  
  const loadMore = useCallback(() => {
    if (data && data?.length > 0) {
      const lastRes = data[data?.length - 1];
      if (lastRes && lastRes.current_page * lastRes.items_per_page < lastRes.count) {
        setSize((prev) => prev + 1);
      }
    }
  }, [data, setSize]);
  
  const renderRow = ({index, style}: {
    index: number;
    style: CSSProperties
  }) => {
    const item = listData[index];
    if (item) return renderItem(item, style)
    return null;
  };
  
  return (<div className={"list-container"}>
    <FixedSizeList
      height={height}
      itemCount={listData.length + 1}
      itemSize={itemSize}
      width="100%"
      className={"list"}
      onItemsRendered={({visibleStopIndex}) => {
        if (!dataProp && visibleStopIndex === listData.length - 1) {
          loadMore();
        }
      }}
    >
      {renderRow}
    </FixedSizeList>
    {isLoading ? <LoadingSpinner className={"spinner"} /> : (!listData.length &&
        <span className={"nodata"}>데이터가 없습니다</span>)}
  </div>);
}

export default forwardRef(InfiniteList) as <T>(props: InfiniteListProps<T> & {
  ref?: React.ForwardedRef<HTMLUListElement>
}) => ReturnType<typeof InfiniteList>;
